import { useEffect, useRef } from 'react';

/**
 * Custom hook to trigger a callback when any scroll-like event (scroll, wheel, touchmove)
 * occurs on the window, document, or scrollable ancestors of a given element ref.
 * 
 * @param {React.RefObject} elementRef - Ref of the element whose scrollable ancestors should be tracked.
 * @param {Function} onClose - Callback function to invoke when scrolling is detected.
 */
export const useCloseOnScroll = (elementRef, onClose) => {
  const onCloseRef = useRef(onClose);
  const timeoutRef = useRef(null);

  // Keep ref updated with the latest onClose callback
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!onClose) return undefined;

    const handleScrollLikeEvent = (event) => {
      document.body.classList.add('is-scrolling');
      if (onCloseRef.current) {
        onCloseRef.current(event);
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        document.body.classList.remove('is-scrolling');
        timeoutRef.current = null;
      }, 100);
    };

    const listenerOptions = { passive: true, capture: true };
    const scrollTargets = new Set([window, document, document.body, document.documentElement]);

    if (elementRef && elementRef.current) {
      let current = elementRef.current.parentElement;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        if (
          (style?.overflowY && /(auto|scroll|overlay)/.test(style.overflowY)) ||
          (style?.overflowX && /(auto|scroll|overlay)/.test(style.overflowX)) ||
          (style?.overflow && /(auto|scroll|overlay)/.test(style.overflow))
        ) {
          scrollTargets.add(current);
        }
        current = current.parentElement;
      }
    }

    scrollTargets.forEach((target) => {
      target.addEventListener('wheel', handleScrollLikeEvent, listenerOptions);
      target.addEventListener('touchmove', handleScrollLikeEvent, listenerOptions);
      target.addEventListener('scroll', handleScrollLikeEvent, listenerOptions);
    });

    return () => {
      scrollTargets.forEach((target) => {
        target.removeEventListener('wheel', handleScrollLikeEvent, listenerOptions);
        target.removeEventListener('touchmove', handleScrollLikeEvent, listenerOptions);
        target.removeEventListener('scroll', handleScrollLikeEvent, listenerOptions);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        document.body.classList.remove('is-scrolling');
      }
    };
  }, [elementRef]);
};

export default useCloseOnScroll;
